import React, { useEffect, useRef, useState } from 'react'
import { Button, List, Modal, Progress, Space, Typography } from 'antd'
import {
  CheckCircleTwoTone,
  ClockCircleOutlined,
  CloseCircleTwoTone,
  LoadingOutlined,
} from '@ant-design/icons'
import { PrintArgs, usePrinter } from './printer.hook'
import { notify, translate } from '../../index'

type TagStatus = 'pending' | 'sending' | 'success' | 'failed'

type QueueJob = PrintArgs<Record<string, string>>

const AUTO_CLOSE_MS = 2000

/**
 * Sequential print queue with live progress.
 *
 * A single job is sent exactly as before (one POST, no modal) so the common
 * case — watches, stones, and 1–2 stone jewelry — is unchanged. When more than
 * one tag is generated (jewelry with 3+ stones) a small non-blocking modal
 * reports per-tag progress, auto-closes on full success, and offers a retry for
 * any tags that failed.
 */
export const usePrintQueue = () => {
  const { print } = usePrinter()

  const [open, setOpen] = useState(false)
  const [jobs, setJobs] = useState<QueueJob[]>([])
  const [statuses, setStatuses] = useState<TagStatus[]>([])
  const [running, setRunning] = useState(false)

  // Synchronous re-entry guard: state updates are async, so a rapid second
  // click could start a second send loop before `running` re-renders. The ref
  // blocks that immediately, preventing duplicate physical tags.
  const runningRef = useRef(false)
  // Set when the user cancels; the send loop stops before the next request.
  const cancelledRef = useRef(false)

  const setStatus = (index: number, status: TagStatus) =>
    setStatuses(prev => {
      const next = [...prev]
      next[index] = status
      return next
    })

  const sendIndices = async (indices: number[], allJobs: QueueJob[]) => {
    for (const index of indices) {
      if (cancelledRef.current)
        break
      setStatus(index, 'sending')
      try {
        await print(allJobs[index])
        setStatus(index, 'success')
      } catch (e) {
        setStatus(index, 'failed')
      }
    }
  }

  const run = async (nextJobs: QueueJob[]) => {
    if (runningRef.current || nextJobs.length === 0)
      return

    runningRef.current = true
    setRunning(true)
    try {
      // Single tag: preserve today's behavior exactly — one request, no modal.
      if (nextJobs.length === 1) {
        try {
          await print(nextJobs[0])
        } catch (e) {
          notify.error(translate('printer'), translate('print_failed_message'))
        }
        return
      }

      cancelledRef.current = false
      setJobs(nextJobs)
      setStatuses(nextJobs.map(() => 'pending'))
      setOpen(true)
      await sendIndices(nextJobs.map((_, i) => i), nextJobs)
    } finally {
      runningRef.current = false
      setRunning(false)
    }
  }

  const retryFailed = async () => {
    if (runningRef.current)
      return

    const failed = statuses
      .map((status, index) => (status === 'failed' ? index : -1))
      .filter(index => index >= 0)

    if (!failed.length)
      return

    runningRef.current = true
    setRunning(true)
    try {
      cancelledRef.current = false
      await sendIndices(failed, jobs)
    } finally {
      runningRef.current = false
      setRunning(false)
    }
  }

  // Stop sending remaining tags and dismiss the modal. An in-flight request
  // cannot be aborted (the transport has no AbortController), but no further
  // tags are sent — this rescues the UI if the printer stops responding.
  const cancel = () => {
    cancelledRef.current = true
    setOpen(false)
  }

  const total = statuses.length
  const successCount = statuses.filter(s => s === 'success').length
  const failedCount = statuses.filter(s => s === 'failed').length
  const allSucceeded = total > 0 && successCount === total
  const finished = !running && total > 0 && statuses.every(s => s === 'success' || s === 'failed')

  // Auto-close once every tag has printed successfully.
  useEffect(() => {
    if (!open || !allSucceeded)
      return

    const timer = setTimeout(() => setOpen(false), AUTO_CLOSE_MS)
    return () => clearTimeout(timer)
  }, [open, allSucceeded])

  const iconFor = (status: TagStatus) => {
    switch (status) {
      case 'success':
        return <CheckCircleTwoTone twoToneColor="#52c41a" />
      case 'failed':
        return <CloseCircleTwoTone twoToneColor="#ff4d4f" />
      case 'sending':
        return <LoadingOutlined />
      default:
        return <ClockCircleOutlined style={{ color: '#bfbfbf' }} />
    }
  }

  const labelFor = (status: TagStatus, index: number) => {
    const tag = `${translate('tag')} ${index + 1} ${translate('of')} ${total}`
    switch (status) {
      case 'success':
        return `${tag} ${translate('sent_successfully')}`
      case 'failed':
        return `${tag} ${translate('failed')}`
      case 'sending':
        return `${translate('sending')} ${tag}…`
      default:
        return tag
    }
  }

  const modal = (
    <Modal
      title={translate('printing_jewelry_tags')}
      open={open}
      mask={false}
      maskClosable={false}
      closable={finished}
      onCancel={finished ? () => setOpen(false) : cancel}
      footer={
        !finished
          ? [
            <Button key="cancel" onClick={cancel}>
              {translate('cancel')}
            </Button>,
          ]
          : failedCount > 0
            ? [
              <Button key="close" onClick={() => setOpen(false)}>
                {translate('close')}
              </Button>,
              <Button key="retry" type="primary" onClick={retryFailed}>
                {translate('retry')}
              </Button>,
            ]
            : null
      }
      width={380}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Progress
          percent={total ? Math.round((successCount / total) * 100) : 0}
          status={failedCount > 0 && finished ? 'exception' : running ? 'active' : 'success'}
        />

        <List
          size="small"
          dataSource={statuses.map((status, index) => ({ status, index }))}
          rowKey={item => item.index}
          renderItem={({ status, index }) => (
            <List.Item>
              <Space>
                {iconFor(status)}
                <span>{labelFor(status, index)}</span>
              </Space>
            </List.Item>
          )}
        />

        {running && (
          <Typography.Text type="secondary">
            {translate('waiting_for_printer')}
          </Typography.Text>
        )}

        {finished && allSucceeded && (
          <Typography.Text type="success">
            {translate('all')} {total} {translate('tags_printed_successfully')}
          </Typography.Text>
        )}

        {finished && failedCount > 0 && (
          <Typography.Text type="danger">
            {translate('please_retry_printing')}
          </Typography.Text>
        )}
      </Space>
    </Modal>
  )

  return { run, modal, busy: running }
}
